<template>
  <transition name="slide">
    <b-row>
      <b-col cols="12" xl="12">
        <b-card>
          <template #header>
            <h5 class="m-0">Solicitações</h5>
          </template>
          <b-card-body>
            <div class="filters-section">
              <b-form-group label="Pesquisar" class="search-filter">
                <b-input-group>
                  <b-input-group-prepend>
                    <b-input-group-text>
                      <i class="fas fa-search" aria-hidden="true"></i>
                    </b-input-group-text>
                  </b-input-group-prepend>
                  <b-form-input
                    v-model="filter"
                    type="search"
                    placeholder="Digite para pesquisar"
                    @input="debounceFilter"
                    @keyup.enter="applyFilter"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-button :disabled="!filter" @click="clearFilter"
                      >Limpar</b-button
                    >
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </div>

            <div class="table-container">
              <b-table
                :items="data.docs"
                :fields="headers"
                :busy="loading"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :tbody-tr-class="rowClass"
                thead-class="dsar-header"
                borderless
                hover
                responsive
                show-empty
                no-local-sorting
                @sort-changed="onSortChanged"
              >
                <template #cell(id)="slotData">
                  <span>{{ slotData.item._id }}</span>
                </template>
                <template #cell(company)="slotData">
                  <span class="company-name">{{
                    slotData.item.company.fantasyName
                  }}</span>
                </template>
                <template #cell(protocol)="slotData">
                  <strong class="protocol-link">{{
                    slotData.item.protocol
                  }}</strong>
                </template>
                <template #cell(createdAt)="slotData">
                  <span class="date-text">{{
                    slotData.item.createdAt | datetime
                  }}</span>
                </template>
                <template #cell(updatedAt)="slotData">
                  <span class="date-text">{{
                    slotData.item.updatedAt | datetime
                  }}</span>
                </template>
                <template #cell(deadline)="slotData">
                  <span class="date-text">{{
                    getDeadline(slotData.item) | datetime
                  }}</span>
                </template>
                <template #cell(status)="slotData">
                  <b-badge :variant="getBadgeColor(slotData.item.status)">
                    {{ getStatusText(slotData.item.status) }}
                  </b-badge>
                </template>
                <template #cell(actions)="slotData">
                  <b-button
                    size="sm"
                    variant="link"
                    class="action-button"
                    title="Editar"
                    @click.stop="edit(slotData.item)"
                  >
                    <i class="fa fa-edit action-icon" aria-hidden="true"></i>
                  </b-button>
                </template>

                <template #table-busy>
                  <div class="text-center py-5">
                    <b-spinner></b-spinner>
                    <span class="ml-2">Carregando...</span>
                  </div>
                </template>
                <template #empty>
                  <div class="text-muted text-center py-5">
                    <i class="fas fa-file-contract fa-2x"></i>
                    <p class="mt-3">Nenhuma solicitação encontrada</p>
                  </div>
                </template>
              </b-table>
            </div>

            <nav class="pagination-nav">
              <b-pagination
                v-model="data.page"
                :total-rows="data.total"
                :per-page="data.limit"
                prev-text="Anterior"
                next-text="Próximo"
                align="center"
              />
            </nav>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </transition>
</template>

<script>
import { getDSARList } from "../../api/dsar";
import { mapGetters } from "vuex";
import { getBadgeColor, getStatusText } from "../complaints/utils";
import DSARStatus from "../../../../backend/models/dsar/DSARStatus.ts";
import { getDeadline } from "./dsar-utils";

export default {
  name: "DSARList",
  data() {
    return {
      DSARStatus,
      sortBy: "createdAt",
      sortDesc: true,
      filter: "",
      loading: false,
      filterTimeout: null,
      data: {
        docs: [],
        total: 0,
        page: 1,
        pages: 0,
        limit: 15,
      },
    };
  },
  computed: {
    ...mapGetters(["profile"]),
    headers() {
      const headers = [];
      if (this.profile.role === "root") {
        headers.push({ key: "id" });
      }
      if (
        this.profile.role === "root" ||
        this.profile.managedCompanies.length
      ) {
        headers.push({
          key: "company",
          label: "Empresa",
          thStyle: { "min-width": "200px !important" },
          sortable: true,
        });
      }
      headers.push(
        { key: "protocol", label: "Protocolo", sortable: true },
        {
          key: "createdAt",
          label: "Data de Recebimento",
          thStyle: { width: "180px !important" },
          sortable: true,
        },
        {
          key: "updatedAt",
          label: "Última Movimentação",
          thStyle: { width: "180px !important" },
          sortable: true,
        },
        {
          key: "deadline",
          label: "Prazo",
          thStyle: { width: "180px !important" },
          sortable: true,
        },
        { key: "status", label: "Status", sortable: true },
        {
          key: "actions",
          label: "Ações",
          thStyle: { width: "50px !important" },
        }
      );
      return headers;
    },
  },
  watch: {
    "data.page": "fetchDSAR",
  },
  mounted() {
    this.fetchDSAR();
  },
  beforeDestroy() {
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
  },
  methods: {
    rowClass(item) {
      if (!item) return "";
      const baseClass = "dsar-row";
      return item.status === DSARStatus.CONCLUDED
        ? `${baseClass} dsar-concluded`
        : baseClass;
    },
    onSortChanged(sortOptions) {
      if (sortOptions.sortBy) {
        this.sortBy = sortOptions.sortBy;
        this.sortDesc = sortOptions.sortDesc || false;
      } else {
        this.sortBy = null;
        this.sortDesc = false;
      }
      this.fetchDSAR();
    },
    applyFilter() {
      this.data.page = 1;
      this.fetchDSAR();
    },
    debounceFilter() {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => {
        this.applyFilter();
      }, 300);
    },
    clearFilter() {
      this.filter = "";
      this.applyFilter();
    },
    getBadgeColor,
    getStatusText,
    getDeadline,
    dsarLink(id) {
      return `lgpd/${id.toString()}`;
    },

    edit(item) {
      const dsarLink = this.dsarLink(item._id);
      this.$router.push({ path: dsarLink });
    },

    async fetchDSAR() {
      this.loading = true;
      try {
        const query = {
          page: this.data.page,
          size: this.data.limit,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
          filter: this.filter || undefined,
        };

        const response = await getDSARList(query);
        if (response && response.data) {
          if (Array.isArray(response.data)) {
            this.data.docs = response.data;
            this.data.total = response.total || response.data.length;
          } else if (response.data.docs) {
            this.data.docs = response.data.docs;
            this.data.total = response.data.total || 0;
            this.data.pages = response.data.pages || 1;
          } else {
            this.data.docs = [];
            this.data.total = 0;
          }
        } else {
          this.data.docs = [];
          this.data.total = 0;
        }
      } catch (error) {
        console.error("Erro ao carregar solicitações:", error);
        let errorMessage = "Erro ao carregar solicitações";
        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        this.$bvToast.toast(errorMessage, {
          title: "Erro",
          variant: "danger",
          solid: true,
        });
        this.data.docs = [];
        this.data.total = 0;
      } finally {
        this.loading = false;
      }
    },

    // Método legado para compatibilidade - será removido quando não for mais necessário
    async retrieveData(ctx) {
      // Redirecionar para o novo método
      await this.fetchDSAR();
      return this.data.docs;
    },
  },
};
</script>

<style scoped>
.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.filters-section {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: flex-end;
}

.search-filter {
  flex: 1;
}

.filters-section .form-group {
  margin-bottom: 0;
}

.filters-section .form-control,
.filters-section .custom-select {
  border-radius: 6px;
  border: 1px solid #e9ecef;
  background-color: #fff;
  color: #495057;
  height: calc(1.5em + 1rem + 2px);
  padding: 0.5rem 1rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.filters-section .form-control:focus,
.filters-section .custom-select:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.2);
  outline: 0;
}

.filters-section .input-group-text {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-right: 0;
  border-radius: 6px 0 0 6px;
}

.filters-section .form-group label {
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  color: #495057;
  margin-bottom: 0.5rem;
}

.pagination-nav {
  margin-top: 1.5rem;
}

::v-deep .dsar-header th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
  font-size: 0.8rem;
  text-transform: uppercase;
  border: 0;
  white-space: nowrap;
}

::v-deep .dsar-header th.sortable:hover {
  color: #0056b3;
}

::v-deep .dsar-row {
  cursor: pointer;
  border-bottom: 1px solid #f1f3f4;
  transition: background-color 0.2s ease-in-out;
}

::v-deep .dsar-row:hover {
  background-color: #e9ecef !important;
}

::v-deep .dsar-row.dsar-concluded {
  background-color: rgba(40, 167, 69, 0.1);
}

::v-deep tbody tr:last-of-type {
  border-bottom: none;
}

::v-deep .dsar-row td {
  border-top: none;
  vertical-align: middle;
  padding: 0.85rem 0.75rem;
}

::v-deep .company-name,
::v-deep .date-text {
  color: #6c757d;
}

::v-deep .protocol-link {
  color: #007bff;
  font-weight: 500;
}

::v-deep .action-icon {
  color: #6c757d;
  transition: color 0.2s;
}

::v-deep .dsar-row:hover .action-icon {
  color: #007bff;
}

::v-deep .page-item .page-link {
  border-radius: 6px;
  margin: 0 3px;
  border: 1px solid #e9ecef;
  color: #495057;
  transition: background-color 0.2s, color 0.2s;
}

::v-deep .page-item .page-link:hover {
  background-color: #f0f2f5;
  color: #0056b3;
}

::v-deep .page-item.active .page-link {
  background-color: #007bff;
  border-color: #007bff;
  color: #fff;
}

::v-deep .page-item.disabled .page-link {
  background-color: transparent;
  color: #adb5bd;
}
</style>
